

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewCouponModal from 'components/NewCouponModal';

const Coupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('coupons');

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/coupons/');
            setCoupons(response.data);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            setSnackbar({ open: true, message: 'Error loading coupons', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCoupon = async (payload) => {
        try {
            await axiosInstance.post('/coupons/', payload);
            setSnackbar({ open: true, message: 'Coupon created successfully', severity: 'success' });
            fetchCoupons();
        } catch (error) {
            console.error('Error saving coupon:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/coupon/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Coupon Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Code",
            accessor: "code",
            width: "10%"
        },
        {
            Header: "Trip",
            accessor: "trip_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id
        },
        {
            Header: "Departure Date",
            accessor: "departure_date_id",
            width: "12%",
            Cell: ({ row }) => row.original.departure_date?.name || row.original.departure_date_id
        },
        {
            Header: "Discount Type",
            accessor: "discount_type",
            width: "10%",
            Cell: ({ value }) => value || 'Fixed'
        },
        {
            Header: "Value",
            accessor: "value",
            width: "10%",
            Cell: ({ value }) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
        },
        {
            Header: "Currency",
            accessor: "currency",
            width: "8%"
        },
        {
            Header: "Available",
            accessor: "available_count",
            width: "8%"
        },
        {
            Header: "Used",
            accessor: "used_count",
            width: "8%"
        },
        {
            Header: "Status",
            accessor: "expiration_date",
            width: "10%",
            Cell: ({ value, row }) => {
                const expDate = value ? new Date(value) : null;
                const now = new Date();
                const isExpired = expDate && expDate < now;
                const isActive = row.original.available_count > (row.original.used_count || 0);
                return (
                    <Chip
                        label={isExpired ? 'Expired' : (isActive ? 'Active' : 'Used Up')}
                        size="small"
                        color={isExpired ? 'error' : (isActive ? 'success' : 'default')}
                    />
                );
            }
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: coupons,
    }), [coupons]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Coupons</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Coupon
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : coupons.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Coupon" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewCouponModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCoupon}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default Coupons;

